import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { TypeToConfirm } from "../TypeToConfirm";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  strategyName: string;
  onConfirmed: () => void;
}

const GATE_KEYS = [
  "gateGlobal",
  "gate90Days",
  "gateHitRate",
  "gateBrier",
  "gateNoIncidents",
  "gateRiskTested",
  "gateBackupRestore",
  "gateSignature",
] as const;

export function LiveEnableConfirmDialog({
  open,
  onOpenChange,
  strategyName,
  onConfirmed,
}: Props) {
  const { t } = useTranslation("admin-strategies");
  const phrase = t("liveConfirm.typeToConfirmPhrase");
  const [typed, setTyped] = useState("");
  const matches = typed === phrase;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("liveConfirm.title")}</DialogTitle>
          <DialogDescription>
            {t("liveConfirm.lead", { name: strategyName })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="rounded-md border p-3">
            <div className="mb-2 text-sm font-medium">{t("liveConfirm.gatesTitle")}</div>
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {GATE_KEYS.map((key) => (
                <li key={key} className="flex items-start gap-2">
                  <span className="mt-0.5 text-muted-foreground">•</span>
                  <span>{t(`liveConfirm.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <Alert className="border-amber-500/30 bg-amber-500/10">
            <AlertDescription className="text-amber-200">
              {t("liveConfirm.gateUnmet")}
            </AlertDescription>
          </Alert>

          <TypeToConfirm
            expected={phrase}
            value={typed}
            onChange={setTyped}
            label={t("liveConfirm.typeToConfirmLabel", { phrase })}
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            {t("liveConfirm.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!matches}
            onClick={onConfirmed}
            data-testid="live-confirm-confirm"
          >
            {t("liveConfirm.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
